/**
 * Data Flow Sankey Visualization Component (Step 6)
 *
 * Displays a 5-layer Sankey diagram showing data flow from source systems
 * to Neo4j Knowledge Graph schema:
 *
 * Layer 0: Enterprise Systems (SAP, Kinaxis, etc.)
 * Layer 1: Modules (MM, SD, Demand Planning, etc.)
 * Layer 2: Source Tables
 * Layer 3: Source Columns
 * Layer 4: Schema Nodes (Neo4j entities)
 *
 * Features:
 * - Interactive node selection
 * - Strategic question coverage indicators
 * - Mapping confidence visualization (HIGH=green, MEDIUM=amber, LOW=red)
 * - Unmapped column recommendations
 */

import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import D3SankeyDiagram from './D3SankeyDiagram';
import {
  Database,
  Layers,
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  RefreshCw,
  Save,
  Download,
  Filter,
  Eye,
  EyeOff,
  ChevronDown,
  Brain,
  Zap,
  MinusCircle,
} from 'lucide-react';
import { Card, CardBody as CardContent, CardHeader } from '../ui/Card';
import Button from '../ui/Button';
import { Badge } from '../ui/Badge';
import Spinner from '../ui/Spinner';
import {
  getDataFlow,
  generateDataFlow,
  getCoverageSummary,
  getUnmappedColumns,
  updateColumnMapping,
  saveDataFlow,
} from '../../lib/api/designPhaseApi';
import { extractErrorMessage, extractAdvisories } from '../../lib/apiErrors';
import type {
  DataFlowVisualization,
  SankeyNode,
  SankeyLink,
  ColumnMapping,
  UnmappedColumn,
  CoverageSummary,
  QuestionCoverageDetail,
  MappingStatus,
} from '../../types/designPhase';

interface DataFlowSankeyProps {
  caseId: string;
  onMappingSelect?: (mapping: ColumnMapping) => void;
  onMappingUpdate?: (mapping: ColumnMapping) => void;
}

// Layer names for filtering
const LAYER_NAMES = ['Systems', 'Modules', 'Tables', 'Columns', 'Schema'];

// Confidence color mapping
const CONFIDENCE_COLORS = {
  high: '#22c55e', // green-500
  medium: '#f59e0b', // amber-500
  low: '#ef4444', // red-500
};

// Node type colors
const NODE_COLORS = {
  system: '#3b82f6', // blue-500
  module: '#8b5cf6', // purple-500
  table: '#06b6d4', // cyan-500
  column: '#64748b', // slate-500
  schema_node: '#10b981', // emerald-500
  schema_relationship: '#f43f5e', // rose-500
};


export const DataFlowSankey: React.FC<DataFlowSankeyProps> = ({
  caseId,
  onMappingSelect,
  onMappingUpdate,
}) => {
  const queryClient = useQueryClient();
  const [selectedLayer, setSelectedLayer] = useState<number | null>(null);
  const [showUnmapped, setShowUnmapped] = useState(true);
  const [selectedNode, setSelectedNode] = useState<SankeyNode | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch data flow visualization
  const {
    data: flowData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['dataFlow', caseId],
    queryFn: () => getDataFlow(caseId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch coverage summary
  const { data: coverageSummary } = useQuery({
    queryKey: ['dataFlowCoverage', caseId],
    queryFn: () => getCoverageSummary(caseId),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch unmapped columns
  const { data: unmappedColumns } = useQuery({
    queryKey: ['unmappedColumns', caseId],
    queryFn: () => getUnmappedColumns(caseId),
    staleTime: 5 * 60 * 1000,
  });

  // Generate data flow mutation
  const generateMutation = useMutation({
    mutationFn: (options: { force_regenerate?: boolean }) =>
      generateDataFlow(caseId, { force_regenerate: options.force_regenerate }),
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['dataFlow', caseId] });
      queryClient.invalidateQueries({ queryKey: ['dataFlowCoverage', caseId] });
      queryClient.invalidateQueries({ queryKey: ['unmappedColumns', caseId] });
      setIsGenerating(false);

      // A 200 that produced nothing is a failure from the user's point of
      // view. The backend explains why in `mapping_recommendations`; say so
      // instead of silently re-rendering the same empty state.
      if (result && (result.total_source_columns === 0 || (result.nodes?.length ?? 0) === 0)) {
        const advisories = extractAdvisories(result);
        toast.warning('No data flow could be generated', {
          description: advisories[0] ?? 'The generator returned no source columns for this case.',
        });
      }
    },
    onError: (err) => {
      setIsGenerating(false);
      toast.error('Data flow generation failed', {
        description: extractErrorMessage(err),
      });
    },
  });

  // Update mapping mutation
  const updateMappingMutation = useMutation({
    mutationFn: ({
      mappingId,
      update,
    }: {
      mappingId: string;
      update: { status: MappingStatus };
    }) => updateColumnMapping(caseId, mappingId, update),
    onSuccess: (updatedMapping) => {
      queryClient.invalidateQueries({ queryKey: ['dataFlow', caseId] });
      onMappingUpdate?.(updatedMapping);
    },
  });

  // Persist the flow currently on screen. Until this runs there is no
  // (:DataFlowMapping) node, so Step 7 and Step 8 have nothing to read and fall
  // back to regenerating a flow the user never reviewed. Sends the mappings
  // as displayed and echoes visualization_id back so repeated saves update the
  // same node instead of accumulating new ones.
  const saveMutation = useMutation({
    mutationFn: () =>
      saveDataFlow(caseId, {
        column_mappings: flowData?.column_mappings,
        relationship_mappings: flowData?.relationship_mappings,
        visualization_id: flowData?.visualization_id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataFlow', caseId] });
      queryClient.invalidateQueries({ queryKey: ['dataFlowCoverage', caseId] });
    },
  });

  // Get filtered Sankey data based on selected layer
  const getFilteredData = useCallback(() => {
    if (!flowData?.nodes || !flowData?.links) {
      return { nodes: [], links: [] };
    }

    // Filter by selected layer if applicable
    let filteredNodes = flowData.nodes;
    let filteredLinks = flowData.links;

    if (selectedLayer !== null) {
      const adjacentLayers = [selectedLayer - 1, selectedLayer, selectedLayer + 1].filter(
        (l) => l >= 0 && l <= 4
      );
      filteredNodes = flowData.nodes.filter((n) => adjacentLayers.includes(n.layer));
      const nodeIds = new Set(filteredNodes.map((n) => n.node_id));
      filteredLinks = flowData.links.filter(
        (l) => nodeIds.has(l.source) && nodeIds.has(l.target)
      );
    }

    return { nodes: filteredNodes, links: filteredLinks };
  }, [flowData, selectedLayer]);

  // Handle node click from D3 Sankey
  const handleNodeClick = useCallback(
    (node: { node_id: string; label: string; layer: number; node_type: string; color: string; metadata?: Record<string, unknown>; questions_count: number }) => {
      // Convert to SankeyNode format for state
      const sankeyNode: SankeyNode = {
        ...node,
        node_type: node.node_type as SankeyNode['node_type'],
        metadata: node.metadata || {},
      };
      setSelectedNode(sankeyNode);
      // If it's a column node, find the mapping
      if (node.node_type === 'column') {
        const mapping = flowData?.column_mappings.find(
          (m) => m.source_column === node.label
        );
        if (mapping) {
          onMappingSelect?.(mapping);
        }
      }
    },
    [flowData, onMappingSelect]
  );

  // Handle generate button click
  const handleGenerate = useCallback(
    (forceRegenerate: boolean = false) => {
      setIsGenerating(true);
      generateMutation.mutate({ force_regenerate: forceRegenerate });
    },
    [generateMutation]
  );

  // Handle unmapped column click
  const handleUnmappedClick = useCallback(
    (column: UnmappedColumn) => {
      // Create a partial mapping for the user to complete
      const partialMapping: Partial<ColumnMapping> = {
        source_system_id: column.system_id,
        source_system_name: column.system_name,
        source_module: column.module_name,
        source_table: column.table_name,
        source_column: column.column_name,
        source_data_type: column.data_type,
        confidence: 'low',
        status: 'manual',
      };
      // Pass to parent for editing
      onMappingSelect?.(partialMapping as ColumnMapping);
    },
    [onMappingSelect]
  );

  // Render coverage badge.
  //
  // `not_applicable` is emitted for a question that maps to no KG area — one no
  // schema could answer — so it is excluded from the coverage denominator
  // rather than scored zero. It was missing from `variants`, and the lookup was
  // unguarded, so destructuring `icon` off `undefined` threw and React
  // unmounted the entire design wizard: a blank page whenever one appeared in
  // the five questions rendered here (Airtel's sq_002 was the first row).
  // The `?? unknown` fallback keeps any future backend value from blanking it
  // again.
  const renderCoverageBadge = (coverage: string) => {
    const variants: Record<string, { className: string; icon: typeof CheckCircle }> = {
      full: { className: 'bg-green-100 text-green-800', icon: CheckCircle },
      partial: { className: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
      none: { className: 'bg-red-100 text-red-800', icon: HelpCircle },
      not_applicable: { className: 'bg-neutral-100 text-neutral-600', icon: MinusCircle },
    };
    const unknown = { className: 'bg-neutral-100 text-neutral-600', icon: HelpCircle };
    const { className, icon: Icon } = variants[coverage] ?? unknown;
    return (
      <Badge className={`flex items-center gap-1 ${className}`}>
        <Icon className="w-3 h-3" />
        <span className="capitalize">{String(coverage).replace(/_/g, ' ')}</span>
      </Badge>
    );
  };

  // Non-fatal diagnostics the backend attached to a 2xx response. When the
  // generator can't produce a flow it still returns 200, with the reason in
  // `mapping_recommendations` — surfaced in the empty state below.
  const emptyStateAdvisories = extractAdvisories(flowData);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Spinner className="w-8 h-8 text-primary-500 mx-auto" />
          <p className="mt-2 text-neutral-600">Loading data flow visualization...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-6">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto" />
            <h3 className="mt-2 text-lg font-semibold text-red-700">Failed to load data flow</h3>
            {/* Prefer the backend's own `detail` over axios' generic
                "Request failed with status code 422". */}
            <p className="text-sm text-red-600 mt-1 whitespace-pre-wrap">
              {extractErrorMessage(error)}
            </p>
            <Button
              onClick={() => handleGenerate(true)}
              className="mt-4"
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <Spinner className="w-4 h-4 mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate Data Flow
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state - no data yet
  if (!flowData || flowData.nodes.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <Layers className="w-16 h-16 text-neutral-300 mx-auto" />
            <h3 className="mt-4 text-lg font-semibold text-neutral-700">
              No Data Flow Mapping Yet
            </h3>
            <p className="text-sm text-neutral-500 mt-2 max-w-md mx-auto">
              Generate a data flow visualization to see how source columns map to your Neo4j
              knowledge graph schema.
            </p>

            {/* The generator answers 200-with-an-empty-result and explains why
                in `mapping_recommendations` (e.g. "No completed schema found.
                Complete the..."). Rendering only the generic empty state threw
                that diagnosis away and left the user with no next step. */}
            {emptyStateAdvisories.length > 0 && (
              <div className="mt-6 max-w-xl mx-auto text-left rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-amber-900">
                      Why this is empty
                    </p>
                    <ul className="mt-1 space-y-1 text-sm text-amber-800 list-disc list-inside">
                      {emptyStateAdvisories.map((message, i) => (
                        <li key={i} className="break-words">{message}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            <Button
              onClick={() => handleGenerate(false)}
              className="mt-6"
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <Spinner className="w-4 h-4 mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Generate Data Flow
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-xl font-bold text-neutral-900 flex items-center gap-2">
                <Database className="w-6 h-6 text-blue-600" />
                Data Flow Mapping
              </h2>
              <p className="text-sm text-neutral-600 mt-1">
                Source systems <ArrowRight className="w-4 h-4 inline mx-1" /> Neo4j Knowledge Graph
              </p>
            </div>

            <div className="flex items-center gap-6">
              {/* Coverage stats */}
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {flowData.mapped_columns || 0}
                </div>
                <div className="text-xs text-neutral-500">Mapped</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">
                  {unmappedColumns?.length || 0}
                </div>
                <div className="text-xs text-neutral-500">Unmapped</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {Math.round((coverageSummary?.overall_score || 0) * 100)}%
                </div>
                <div className="text-xs text-neutral-500">Question Coverage</div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleGenerate(true)}
                  disabled={isGenerating || saveMutation.isPending}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${isGenerating ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => saveMutation.mutate()}
                  disabled={isGenerating || saveMutation.isPending || !flowData}
                  title="Save these mappings so Steps 7 and 8 use them"
                >
                  <Save className={`w-4 h-4 mr-1 ${saveMutation.isPending ? 'animate-pulse' : ''}`} />
                  {saveMutation.isPending ? 'Saving…' : 'Save Data Flow'}
                </Button>
              </div>
            </div>

            {/* Save feedback — a failed save must not look like a successful one. */}
            {saveMutation.isError && (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Could not save the data flow. Your mappings are still on screen — try again.
                </span>
              </div>
            )}
            {saveMutation.isSuccess && !saveMutation.isPending && (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
                <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Saved {saveMutation.data?.column_mappings_saved ?? 0} column mapping
                  {(saveMutation.data?.column_mappings_saved ?? 0) === 1 ? '' : 's'}. Steps 7 and 8
                  will use this version.
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Layer filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-neutral-600 flex items-center gap-1">
          <Filter className="w-4 h-4" />
          Filter by layer:
        </span>
        {LAYER_NAMES.map((layer, idx) => (
          <Button
            key={layer}
            variant={selectedLayer === idx ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setSelectedLayer(selectedLayer === idx ? null : idx)}
            className="min-w-[80px]"
          >
            {layer}
          </Button>
        ))}
        {selectedLayer !== null && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedLayer(null)}
            className="text-neutral-500"
          >
            Clear filter
          </Button>
        )}
      </div>

      {/* Main Sankey visualization */}
      <Card className="overflow-hidden">
        <CardContent className="p-4">
          {(() => {
            const { nodes, links } = getFilteredData();
            return nodes.length > 0 && links.length > 0 ? (
              <D3SankeyDiagram
                nodes={nodes}
                links={links}
                height={600}
                onNodeClick={handleNodeClick}
              />
            ) : (
              <div className="flex items-center justify-center h-[600px] text-neutral-500">
                <p>No data to display for the selected filter.</p>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-6">
              <span className="text-sm font-medium text-neutral-700">Node Types:</span>
              {Object.entries(NODE_COLORS).map(([type, color]) => (
                <div key={type} className="flex items-center gap-1">
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-xs text-neutral-600 capitalize">
                    {type.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-6">
              <span className="text-sm font-medium text-neutral-700">Confidence:</span>
              {Object.entries(CONFIDENCE_COLORS).map(([level, color]) => (
                <div key={level} className="flex items-center gap-1">
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-xs text-neutral-600 capitalize">{level}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Question coverage breakdown */}
      {coverageSummary && coverageSummary.by_question.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-500" />
              Strategic Question Coverage
              <Badge variant="outline" className="ml-2">
                {coverageSummary.questions_fully_covered} / {coverageSummary.total_questions} fully
                covered
              </Badge>
            </h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {coverageSummary.by_question.slice(0, 5).map((q) => (
                <div
                  key={q.question_id}
                  className="flex items-center gap-4 p-2 rounded-lg bg-neutral-50 hover:bg-neutral-100 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate">
                      {q.question_text}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {q.mapped_columns} column{q.mapped_columns !== 1 ? 's' : ''} mapped to{' '}
                      {q.target_entities.length} entit{q.target_entities.length !== 1 ? 'ies' : 'y'}
                    </p>
                  </div>
                  {renderCoverageBadge(q.coverage)}
                </div>
              ))}
              {coverageSummary.by_question.length > 5 && (
                <p className="text-sm text-neutral-500 text-center pt-2">
                  And {coverageSummary.by_question.length - 5} more questions...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unmapped columns with recommendations */}
      {unmappedColumns && unmappedColumns.length > 0 && (
        <Card className={showUnmapped ? 'border-amber-200 bg-amber-50' : ''}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Unmapped Columns
                <Badge variant="outline" className="bg-amber-100 text-amber-700 ml-2">
                  {unmappedColumns.length}
                </Badge>
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowUnmapped(!showUnmapped)}
              >
                {showUnmapped ? (
                  <>
                    <EyeOff className="w-4 h-4 mr-1" />
                    Hide
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-1" />
                    Show
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          {showUnmapped && (
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {unmappedColumns.slice(0, 12).map((col) => (
                  <div
                    key={col.column_id}
                    className="p-3 bg-white rounded-lg border border-amber-200 cursor-pointer hover:border-primary-500 hover:shadow-sm transition-all"
                    onClick={() => handleUnmappedClick(col)}
                  >
                    <p className="text-sm font-mono font-medium text-neutral-900 truncate">
                      {col.column_name}
                    </p>
                    <p className="text-xs text-neutral-500 truncate mt-1">
                      {col.table_name}
                    </p>
                    <p className="text-xs text-neutral-400 truncate">
                      {col.system_name}
                    </p>
                    {col.suggested_mappings.length > 0 && (
                      <Badge variant="outline" className="mt-2 text-xs">
                        {col.suggested_mappings.length} suggestion
                        {col.suggested_mappings.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
              {unmappedColumns.length > 12 && (
                <p className="text-sm text-neutral-500 text-center pt-4">
                  And {unmappedColumns.length - 12} more unmapped columns...
                </p>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Selected node details panel */}
      {selectedNode && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">
                {selectedNode.label}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedNode(null)}
              >
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-neutral-500">Type</p>
                <p className="font-medium capitalize">
                  {selectedNode.node_type.replace('_', ' ')}
                </p>
              </div>
              <div>
                <p className="text-neutral-500">Layer</p>
                <p className="font-medium">{LAYER_NAMES[selectedNode.layer]}</p>
              </div>
              {selectedNode.questions_count > 0 && (
                <div className="col-span-2">
                  <p className="text-neutral-500">Questions Served</p>
                  <p className="font-medium text-green-600">
                    {selectedNode.questions_count} strategic question
                    {selectedNode.questions_count !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DataFlowSankey;
